import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import {
  LabelInput,
  LabelSelect,
  MoneyInput,
} from '../../../components/Input/Input';
import { TbEdit, TbPlus } from 'react-icons/tb';
import { FormGroup, Label } from 'reactstrap';
import Select from 'react-select';
import { alertError, alertSuccess } from '../../../components/Toast';
import {
  ENTRY_TYPE,
  INSTALLMENT_STATUS,
  STATUS,
  accountPlanTypeToEntryType,
  isAccountPlanCompatible,
} from '../../../core/constants/financial';
import { loadAccountPlans } from '../../../core/utils/accountPlan';
import { loadBanks } from '../../../core/utils/bank';
import { loadSuppliers } from '../../../core/utils/supplier';
import {
  loadClient,
  loadOrigins,
  saveOrigin,
} from '../../../core/utils/client';
import {
  deleteFinancialEntry,
  loadFinancialEntriesByOS,
  loadFinancialEntryById,
  saveFinancialEntry,
} from '../../../core/utils/financialEntry';
import {
  loadBreakdownByEntry,
  replaceBreakdownRows,
  rescaleBreakdown,
  softDeleteBreakdownByEntry,
} from '../../../core/utils/breakdown';
import { formatLocalISO, todayISO } from '../../../core/utils/financialPeriod';
import { formatCurrency, parseMoneyValue } from '../../../core/utils/format';
import {
  deletePendingInstallmentsByEntry,
  loadInstallmentsByEntry,
  markInstallmentAsPaid,
  revertInstallmentToPending,
  saveInstallment,
  saveInstallments,
  updatePaidInstallment,
} from '../../../core/utils/installment';
import DocumentAttach from './DocumentAttach';
import InstallmentTable from './InstallmentTable';
import SupplierFormModal from './SupplierFormModal';
import BudgetAddClient from '../../BudgetAddClient/BudgetAddClient';

// Monta as opções do select agrupadas por conta-pai (nível 1), exibindo só as
// subcontas (nível 2) compatíveis com o tipo do lançamento.
const buildAccountPlanOptions = (accountPlans, entryType) => {
  const parentsById = new Map(
    accountPlans.filter((a) => a.level === 1).map((a) => [a.id, a])
  );
  const groups = new Map();

  accountPlans
    .filter((a) => a.level === 2 && isAccountPlanCompatible(a.type, entryType))
    .forEach((a) => {
      const parent = parentsById.get(a.parent_id);
      const groupLabel = parent
        ? `${parent.code} - ${parent.name}`
        : 'Outras contas';
      if (!groups.has(groupLabel)) groups.set(groupLabel, []);
      groups
        .get(groupLabel)
        .push({ value: a.id, label: `${a.code} - ${a.name}` });
    });

  return Array.from(groups, ([label, options]) => ({ label, options }));
};

// Resolve o valor exibido no select de fornecedor. Prioriza supplier_id (match
// nas opções); cai para o texto livre legado (supplier_client) quando não há id.
const buildSupplierValue = (form, options) => {
  if (form.supplier_id) {
    const match = options.find((o) => o.value === form.supplier_id);
    if (match) return match;
    if (form.supplier_client) {
      return { value: form.supplier_id, label: form.supplier_client };
    }
    return null;
  }
  if (form.supplier_client) {
    return { value: form.supplier_client, label: form.supplier_client };
  }
  return null;
};

const findSelectedAccountOption = (groupedOptions, accountPlanId) => {
  for (const group of groupedOptions) {
    const match = group.options.find((o) => o.value === accountPlanId);
    if (match) return match;
  }
  return null;
};

// Tema dark do react-select renderizado em portal (mesmo do LabelSelect). Os
// estilos SCSS de .generic-input não atingem o menu no portal (renderizado no
// body), então aplicamos aqui.
const partySelectPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  menu: (base) => ({
    ...base,
    background: '#232323',
    border: '1px solid #383838',
    boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
  }),
  option: (base, state) => ({
    ...base,
    background: state.isSelected
      ? 'rgba(91,196,36,0.18)'
      : state.isFocused
        ? 'rgba(91,196,36,0.10)'
        : 'transparent',
    color: state.isSelected ? '#70E000' : '#fff',
    cursor: 'pointer',
    ':active': { background: 'rgba(91,196,36,0.25)' },
  }),
  singleValue: (base) => ({ ...base, color: '#fff' }),
  input: (base) => ({ ...base, color: '#fff' }),
  placeholder: (base) => ({ ...base, color: '#666' }),
  noOptionsMessage: (base) => ({ ...base, color: '#888' }),
};

const FinancialEntryModal = ({
  isOpen,
  onClose,
  onSaved,
  onDelete,
  entryId,
  prefilled,
}) => {
  const company = useSelector((state) => state.app.company || {});
  const user = useSelector((state) => state.app.user || {});

  const isEdit = !!entryId;

  const [type, setType] = useState(ENTRY_TYPE.DESPESA);
  const [form, setForm] = useState({
    document: '',
    document_url: null,
    issue_date: todayISO(),
    supplier_client: '',
    supplier_id: null,
    account_plan_id: null,
    cost_center_id: null,
    total_value: '',
    description: '',
    order_service_id: null,
  });
  const [installmentCount, setInstallmentCount] = useState(1);
  const [installments, setInstallments] = useState([]);
  // IDs das parcelas que JÁ ESTAVAM PAGAS quando o modal abriu — usado em
  // edição com lockSaldo para não tentar marcar novamente como pago (evita
  // o erro "Parcela já está paga" do markInstallmentAsPaid).
  const [originalPaidIds, setOriginalPaidIds] = useState(new Set());
  // Preenchido em modo edição quando o lançamento faz parte de um rateio de OS
  // LEGADO (outros lançamentos com o mesmo order_service_id): { count, total }.
  const [osRateio, setOsRateio] = useState(null);
  // Breakdown (separação por serviço p/ a DRE) do lançamento em edição. Quando
  // tem linhas, é uma cobrança de OS (modelo novo): tipo/plano ficam travados e
  // o total é re-rateado no save. null enquanto carrega ou fora de OS.
  const [breakdown, setBreakdown] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // Opções dos selects
  const [accountPlans, setAccountPlans] = useState([]);
  const [banks, setBanks] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [clients, setClients] = useState([]);
  const [origins, setOrigins] = useState([]);

  // Controle dos modais de cadastro rápido (mesmo padrão do orçamento):
  // botão verde abre em modo criar (id 0/null), botão azul em modo editar.
  const [supplierModal, setSupplierModal] = useState({ open: false, id: null });
  const [clientModal, setClientModal] = useState({ open: false, id: 0 });
  const [refModalKey, setRefModalKey] = useState(0);

  // Há parcela PAGA em DB? Baseado em originalPaidIds (estado do banco), não no
  // state atual — promover pending→PAGO nesta sessão ainda não moveu saldo.
  // Quando true, ainda travamos o que não é corrigível com segurança: TIPO
  // (inverter sinal de saldo), QTD de parcelas / GERAR (apagaria parcelas pagas)
  // e EXCLUIR. Valor total, valor/vencimento/banco de parcela paga e dados do
  // header ficam EDITÁVEIS — a edição de parcela paga reconcilia o Bank.balance
  // via updatePaidInstallment (RN06). Estorno (PAGO→PENDENTE) é fora de escopo.
  const lockSaldo = isEdit && originalPaidIds.size > 0;
  const lockTooltip = lockSaldo
    ? 'Bloqueado: este lançamento já tem parcela paga.'
    : undefined;

  // Cobrança de OS (modelo novo): 1 lançamento com breakdown por categoria. A
  // classificação (tipo/plano) é definida pela OS e fica travada aqui; editar os
  // valores das parcelas re-rateia o breakdown proporcionalmente ao salvar.
  const isOSCharge = isEdit && Array.isArray(breakdown) && breakdown.length > 0;
  // Tipo e plano de contas de uma cobrança de OS são definidos pela OS — travados.
  const lockClassification = lockSaldo || isOSCharge;
  const classificationTooltip = isOSCharge
    ? 'Classificação definida pela cobrança da OS (veja a separação da DRE abaixo).'
    : lockTooltip;

  // Aplica prefilled (vindo de outras telas, ex: botão "Cobrar Cliente" na OS)
  // sempre que o modal abre. Pula em modo edição: entryId tem prioridade — o
  // useEffect de load preenche o form a partir do DB.
  useEffect(() => {
    if (!isOpen || entryId) return;
    if (prefilled) {
      if (prefilled.type) setType(prefilled.type);
      setForm((prev) => ({
        ...prev,
        document: prefilled.document || '',
        supplier_client: (prefilled.supplier_client || '').toUpperCase(),
        total_value: prefilled.total_value != null ? formatCurrency(prefilled.total_value) : '',
        description: prefilled.description || '',
        order_service_id: prefilled.order_service_id || null,
        issue_date: todayISO(),
        document_url: null,
      }));
    }
  }, [isOpen, prefilled, entryId]);

  useEffect(() => {
    if (!isOpen || !company.id) return;

    let cancelled = false;
    Promise.all([
      loadAccountPlans(company.id),
      loadBanks(company.id),
      loadSuppliers(company.id),
      loadClient(company.id),
      loadOrigins(company.id),
    ]).then(([plans, bankList, supplierList, clientList, originList]) => {
      if (cancelled) return;
      setAccountPlans(plans);
      setBanks(bankList);
      setSuppliers(supplierList || []);
      setClients(clientList || []);
      setOrigins(originList || []);
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, company.id, refModalKey]);

  // Modo edição: carrega lançamento + parcelas do DB quando abre com entryId.
  // Popula form, type e installments. Refaz a busca se entryId mudar.
  useEffect(() => {
    if (!isOpen || !entryId) return;

    let cancelled = false;
    setLoading(true);
    Promise.all([
      loadFinancialEntryById(entryId),
      loadInstallmentsByEntry(entryId),
      loadBreakdownByEntry(entryId),
    ])
      .then(([entry, insts, breakdownRows]) => {
        if (cancelled) return;
        if (!entry) {
          alertError('Lançamento não encontrado.');
          onClose();
          return;
        }
        setBreakdown(breakdownRows && breakdownRows.length > 0 ? breakdownRows : null);
        setType(entry.type || ENTRY_TYPE.DESPESA);
        setForm({
          document: entry.document || '',
          document_url: entry.document_url || null,
          issue_date: entry.issue_date || todayISO(),
          supplier_client: entry.supplier_client || '',
          supplier_id: entry.supplier_id || null,
          account_plan_id: entry.account_plan_id || null,
          cost_center_id: entry.cost_center_id || null,
          total_value: entry.total_value != null ? formatCurrency(entry.total_value) : '',
          description: entry.description || '',
          // Sem isto o save regravava order_service_id como null e o lançamento
          // saía do rateio da OS (quebrava agrupamento e idempotência da cobrança).
          order_service_id: entry.order_service_id || null,
        });
        setOsRateio(null);
        if (entry.order_service_id) {
          loadFinancialEntriesByOS(entry.order_service_id).then((siblings) => {
            if (cancelled || siblings.length <= 1) return;
            setOsRateio({
              count: siblings.length,
              total: siblings.reduce((s, e) => s + (Number(e.total_value) || 0), 0),
            });
          });
        }
        const mapped = (insts || []).map((i) => ({
          id: i.id,
          installment_number: i.installment_number,
          due_date: i.due_date || '',
          value: i.value != null ? formatCurrency(i.value) : '',
          payment_method: i.payment_method || '',
          bank_id: i.bank_id || null,
          status: i.status || INSTALLMENT_STATUS.PENDENTE,
          payment_date: i.payment_date || '',
        }));
        setInstallments(mapped);
        setInstallmentCount(mapped.length || 1);
        setOriginalPaidIds(
          new Set(
            (insts || [])
              .filter((i) => i.status === INSTALLMENT_STATUS.PAGO)
              .map((i) => i.id)
          )
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, entryId]);

  const onChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const getAccountPlanById = (id) =>
    accountPlans.find((a) => a.id === id) || null;

  const accountPlanOptions = buildAccountPlanOptions(accountPlans, type);

  // Fornecedores ativos como opções do select (despesa). O valor selecionado é
  // resolvido por supplier_id; entradas legadas sem id caem no texto livre
  // (supplier_client) mantido como rótulo.
  const supplierOptions = suppliers
    .filter((s) => s.status === STATUS.ATIVO)
    .map((s) => ({ value: s.id, label: s.name }));

  const supplierValue = buildSupplierValue(form, supplierOptions);

  // Clientes (base única do orçamento) como opções do select de receita. Não há
  // coluna client_id no FinancialEntry — o vínculo é feito pelo nome gravado em
  // supplier_client (denormalizado, maiúsculo), casando com a opção na abertura.
  const clientOptions = clients
    .map((c) => ({ value: c.id, label: c.name }))
    .sort((a, b) => (a.label || '').localeCompare(b.label || '', 'pt-BR'));
  const normalizedClient = (form.supplier_client || '').toUpperCase();
  const clientValue =
    clientOptions.find((o) => (o.label || '').toUpperCase() === normalizedClient) ||
    (form.supplier_client
      ? { value: form.supplier_client, label: form.supplier_client }
      : null);
  const selectedClientId =
    clientOptions.find(
      (o) => (o.label || '').toUpperCase() === normalizedClient
    )?.value || null;

  const handleSelectClient = (option) => {
    if (!option) {
      onChange('supplier_client', '');
      return;
    }
    onChange('supplier_client', (option.label || '').toUpperCase());
  };

  // Regrava o nome salvo no form — o vínculo é por nome, não por id.
  const onClientSaved = (saved) => {
    if (!saved || !saved.name) return;
    if (saved.id) {
      setClients((prev) => {
        const exists = prev.some((c) => c.id === saved.id);
        if (exists) {
          return prev.map((c) => (c.id === saved.id ? { ...c, ...saved } : c));
        }
        return [...prev, saved];
      });
    }
    setForm((prev) => ({
      ...prev,
      supplier_client: saved.name.toUpperCase(),
    }));
  };

  // Recebe o fornecedor salvo pelo modal rápido e já o seleciona no lançamento.
  const onSupplierSaved = (saved) => {
    setSupplierModal({ open: false, id: null });
    setRefModalKey((k) => k + 1);
    if (!saved) {
      setForm((prev) => ({ ...prev, supplier_id: null, supplier_client: '' }));
      return;
    }
    setSuppliers((prev) => {
      const exists = prev.some((s) => s.id === saved.id);
      return exists
        ? prev.map((s) => (s.id === saved.id ? saved : s))
        : [saved, ...prev];
    });
    setForm((prev) => ({
      ...prev,
      supplier_id: saved.id,
      supplier_client: (saved.name || '').toUpperCase(),
    }));
  };

  const handleCreateOrigin = async (name) => {
    await saveOrigin({ name, company: company.id });
    const list = await loadOrigins(company.id);
    setOrigins(list || []);
  };

  // Seleção de fornecedor existente (value numérico) ou item legado/digitado
  // (value string). Mantém supplier_client preenchido (denormalizado) p/ telas
  // de contas a pagar / fluxo de caixa.
  const handleSelectSupplier = (option) => {
    if (!option) {
      setForm((prev) => ({ ...prev, supplier_id: null, supplier_client: '' }));
      return;
    }
    const isExisting = typeof option.value === 'number';
    setForm((prev) => ({
      ...prev,
      supplier_id: isExisting ? option.value : null,
      supplier_client: (option.label || '').toUpperCase(),
    }));
  };

  // Campo Cliente/Fornecedor no padrão do orçamento: select da base única +
  // botão verde (adicionar) + botão azul (editar o selecionado). Cliente puxa
  // da tabela Client; Fornecedor da tabela Supplier.
  const addBtnStyle = {
    height: 38,
    minWidth: 42,
    flex: '0 0 auto',
    borderRadius: 8,
    border: 'none',
    background: '#5BC424',
    color: '#fff',
    fontSize: 20,
    lineHeight: 1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  const editBtnStyle = (enabled) => ({
    ...addBtnStyle,
    background: '#6694ec',
    fontSize: 16,
    opacity: enabled ? 1 : 0.45,
    cursor: enabled ? 'pointer' : 'not-allowed',
  });

  const renderSupplierField = () => {
    const isDespesa = type === ENTRY_TYPE.DESPESA;
    const label = isDespesa ? 'Fornecedor' : 'Cliente';
    const options = isDespesa ? supplierOptions : clientOptions;
    const value = isDespesa ? supplierValue : clientValue;
    const onSelect = isDespesa ? handleSelectSupplier : handleSelectClient;
    const editId = isDespesa ? form.supplier_id : selectedClientId;
    const openNew = () =>
      isDespesa
        ? setSupplierModal({ open: true, id: null })
        : setClientModal({ open: true, id: 0 });
    const openEdit = () => {
      if (!editId) return;
      if (isDespesa) setSupplierModal({ open: true, id: editId });
      else setClientModal({ open: true, id: editId });
    };

    // Espelha a estrutura do campo Plano de Contas (FormGroup + Label do
    // reactstrap, idêntico ao LabelSelect) para o label alinhar. Abaixo do
    // label, uma linha única com o select (sem label próprio) + botões, todos
    // na mesma altura e centralizados → botões alinham com o input.
    return (
      <FormGroup className="generic-input select-input" style={{ marginBottom: 0 }}>
        <Label className="label">{label}</Label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Select
              options={options}
              value={value}
              isClearable
              placeholder={`Selecione um ${label.toLowerCase()}`}
              onChange={onSelect}
              menuPortalTarget={document.body}
              styles={partySelectPortalStyles}
              noOptionsMessage={() => 'Não há opções'}
            />
          </div>
          <button
            type="button"
            style={addBtnStyle}
            title={`Adicionar ${label.toLowerCase()}`}
            onClick={openNew}
          >
            <TbPlus style={{ width: 20, height: 20 }} />
          </button>
          <button
            type="button"
            style={editBtnStyle(!!editId)}
            title={`Editar ${label.toLowerCase()}`}
            disabled={!editId}
            onClick={openEdit}
          >
            <TbEdit style={{ width: 18, height: 18 }} />
          </button>
        </div>
      </FormGroup>
    );
  };

  // Painel read-only da separação por serviço (breakdown) de uma cobrança de OS.
  // Substitui o select de Plano de Contas — a classificação vem da OS. Os valores
  // são re-rateados proporcionalmente ao salvar, se o total das parcelas mudar.
  const renderBreakdownPanel = () => {
    const total = breakdown.reduce((s, r) => s + (Number(r.value) || 0), 0);
    return (
      <div style={{ margin: '4px 0 8px' }}>
        <div className="fd-entry-installments-header" style={{ marginTop: 0 }}>
          <span className="fd-entry-installments-title">
            Separação por serviço (usada na DRE)
          </span>
        </div>
        <table className="fd-modal-table" style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Plano de Contas</th>
              <th style={{ textAlign: 'right' }}>Valor</th>
            </tr>
          </thead>
          <tbody>
            {breakdown.map((r) => (
              <tr key={r.id}>
                <td>
                  {r.AccountPlan
                    ? `${r.AccountPlan.code} - ${r.AccountPlan.name}`
                    : '—'}
                </td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(r.value)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>Total</td>
              <td style={{ textAlign: 'right', fontWeight: 700, color: '#70E000' }}>
                {formatCurrency(total)}
              </td>
            </tr>
          </tfoot>
        </table>
        <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
          Editar os valores das parcelas re-rateia esta separação
          proporcionalmente ao salvar. A DRE continua separando por serviço.
        </div>
      </div>
    );
  };

  // Seleciona a conta e infere o tipo do lançamento a partir dela (RN03).
  // Não altera o tipo quando o saldo está travado.
  const handleSelectAccountPlan = (planId) => {
    onChange('account_plan_id', planId);
    const plan = getAccountPlanById(planId);
    if (plan && !lockSaldo) setType(accountPlanTypeToEntryType(plan.type));
  };

  // Troca o tipo e limpa a conta se ela for incompatível com o novo tipo (RN05).
  const handleChangeType = (novoTipo) => {
    if (novoTipo === type) return;
    setType(novoTipo);
    // Cliente e fornecedor compartilham supplier_client — não vaza entre tipos.
    setForm((prev) => ({ ...prev, supplier_id: null, supplier_client: '' }));
    const plan = getAccountPlanById(form.account_plan_id);
    if (plan && !isAccountPlanCompatible(plan.type, novoTipo)) {
      onChange('account_plan_id', null);
    }
  };

  const bankOptions = banks
    .filter((b) => b.status === STATUS.ATIVO)
    .map((b) => ({ value: b.id, label: b.name }));

  const generateInstallments = () => {
    const total = parseMoneyValue(form.total_value);
    if (total <= 0) {
      alertError('Informe o valor total antes de gerar parcelas.');
      return;
    }

    const count = Math.max(1, Math.min(48, installmentCount));
    const baseValue = (total / count).toFixed(2);

    // Parcela 1 vence na data do lançamento (Data Emissão). As demais somam
    // 1 mês cada, sempre a partir da parcela 1.
    const baseISO = form.issue_date || todayISO();
    const [y, m, d] = baseISO.split('-').map(Number);
    const firstDueDate = new Date(y, m - 1, d);
    firstDueDate.setHours(0, 0, 0, 0);

    const newInstallments = Array.from({ length: count }, (_, i) => {
      const dueDate = new Date(firstDueDate);
      if (i > 0) dueDate.setMonth(dueDate.getMonth() + i);
      return {
        installment_number: i + 1,
        due_date: formatLocalISO(dueDate),
        value: formatCurrency(baseValue),
        payment_method: '',
        bank_id: null,
        status: INSTALLMENT_STATUS.PENDENTE,
        payment_date: '',
      };
    });

    setInstallments(newInstallments);
  };

  const resetForm = () => {
    setType(ENTRY_TYPE.DESPESA);
    setForm({
      document: '',
      document_url: null,
      issue_date: todayISO(),
      supplier_client: '',
      supplier_id: null,
      account_plan_id: null,
      cost_center_id: null,
      total_value: '',
      description: '',
      order_service_id: null,
    });
    setInstallments([]);
    setInstallmentCount(1);
    setOriginalPaidIds(new Set());
    setOsRateio(null);
    setBreakdown(null);
  };

  // Zera o state ao fechar — cobre fechamentos vindos do pai (ex.: exclusão).
  useEffect(() => {
    if (!isOpen) resetForm();
  }, [isOpen]);

  // Cobrança de OS: o "Valor Total" acompanha a soma das parcelas (verdade de
  // caixa) — o campo vira derivado, evitando total defasado ao editar parcelas.
  useEffect(() => {
    if (!isOSCharge) return;
    const sum = installments.reduce((s, i) => s + parseMoneyValue(i.value), 0);
    const formatted = formatCurrency(sum);
    setForm((prev) =>
      prev.total_value === formatted ? prev : { ...prev, total_value: formatted }
    );
  }, [isOSCharge, installments]);

  // Exclusão autossuficiente do lançamento — disponível em qualquer tela que
  // abre este modal (Lançamentos do Período, Receitas/Despesas, Contas a Receber,
  // Extrato). Cobrança de OS: some da dashboard e da DRE (soft-delete do
  // breakdown) e libera a exclusão da OS. Bloqueado quando há parcela paga.
  const handleDeleteEntry = async () => {
    if (saving || loading || lockSaldo || !entryId) return;
    const label = form.supplier_client || form.description || 'Lançamento';
    const amount = formatCurrency(parseMoneyValue(form.total_value));
    const confirm = await Swal.fire({
      title: isOSCharge ? 'Excluir cobrança da OS?' : 'Excluir lançamento?',
      html: isOSCharge
        ? `${label} — ${amount}. Remove a cobrança do financeiro (e da DRE) e libera a exclusão da OS. Esta ação não pode ser desfeita.`
        : `${label} — ${amount}. Esta ação não pode ser desfeita.`,
      icon: 'warning',
      showCancelButton: true,
      showDenyButton: true,
      showConfirmButton: false,
      denyButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    });
    if (!confirm.isDenied) return;

    setSaving(true);
    try {
      const { error } = await deleteFinancialEntry(entryId, user.id);
      if (error) {
        alertError('Erro ao excluir lançamento.');
        return;
      }
      await softDeleteBreakdownByEntry(entryId, user.id);
      alertSuccess('Lançamento excluído.');
      if (onSaved) onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (saving || loading) return;

    if (!form.supplier_client || !form.supplier_client.trim()) {
      alertError(
        type === ENTRY_TYPE.RECEITA
          ? 'Cliente é obrigatório.'
          : 'Fornecedor é obrigatório.'
      );
      return;
    }
    if (!form.total_value || parseMoneyValue(form.total_value) <= 0) {
      alertError('Valor total é obrigatório.');
      return;
    }
    if (installments.length === 0) {
      alertError('Gere as parcelas antes de salvar.');
      return;
    }
    // RN-004: banco e forma de pagamento obrigatórios em todas as parcelas.
    const missingPayment = installments.find(
      (i) => !i.bank_id || !i.payment_method
    );
    if (missingPayment) {
      alertError(
        `Parcela ${missingPayment.installment_number} precisa de banco e forma de pagamento.`
      );
      return;
    }
    const missingPaymentDate = installments.find(
      (i) => i.status === INSTALLMENT_STATUS.PAGO && !i.payment_date
    );
    if (missingPaymentDate) {
      alertError(
        `Parcela ${missingPaymentDate.installment_number} está paga e precisa da data de pagamento.`
      );
      return;
    }
    if (!form.account_plan_id) {
      alertError('Plano de contas é obrigatório.');
      return;
    }
    const selectedPlan = getAccountPlanById(form.account_plan_id);
    if (selectedPlan && !isAccountPlanCompatible(selectedPlan.type, type)) {
      alertError('Plano de contas incompatível com o tipo do lançamento.');
      return;
    }

    setSaving(true);
    try {
      // Com lockSaldo só o TIPO fica fora do payload (inverter receita↔despesa
      // mudaria o sinal do saldo já movimentado). Valor total e demais campos
      // são editáveis inclusive em lançamento pago.
      // Padrão Prospera: nomes de cliente/fornecedor sempre em maiúsculo.
      // Normaliza no save pra cobrir registros legados carregados em edição
      // sem o campo ter sido tocado.
      const supplierClient = (form.supplier_client || '').toUpperCase();
      // Cobrança de OS: o total é a soma das parcelas reais (a verdade de caixa),
      // não o campo "Valor Total" — que pode ficar defasado ao editar parcelas.
      const installmentsSum = installments.reduce(
        (s, i) => s + parseMoneyValue(i.value),
        0
      );
      const entryTotalValue = isOSCharge
        ? installmentsSum
        : parseMoneyValue(form.total_value);
      const entryPayload = {
        company_id: company.id,
        document: form.document,
        document_url: form.document_url || null,
        issue_date: form.issue_date || null,
        supplier_client: supplierClient,
        supplier_id: type === ENTRY_TYPE.DESPESA ? form.supplier_id || null : null,
        account_plan_id: form.account_plan_id,
        cost_center_id: form.cost_center_id,
        total_value: entryTotalValue,
        description: form.description,
        order_service_id: form.order_service_id || null,
        ...(lockSaldo ? {} : { type }),
        ...(isEdit ? {} : { created_by: user.id }),
      };

      const { data: entryData, error: entryError } = await saveFinancialEntry(
        isEdit ? entryId : undefined,
        entryPayload,
        user.id
      );
      if (entryError) {
        alertError('Erro ao salvar lançamento.');
        return;
      }

      // INSERT retorna data com o registro novo; UPDATE pode não retornar o id,
      // então em edição usamos o entryId já recebido por prop.
      const savedEntryId = isEdit ? entryId : entryData?.[0]?.id;
      if (!savedEntryId) {
        alertError('Erro ao obter ID do lançamento.');
        return;
      }

      // Marca como PAGO via markInstallmentAsPaid (que move Bank.balance).
      // Sempre inserimos como PENDENTE primeiro e depois "pagamos" — assim
      // reaproveitamos a lógica de saldo já consolidada em PaymentModal.
      const markPaidFor = async (rows, idForIndex) => {
        for (let i = 0; i < rows.length; i++) {
          if (rows[i].status !== INSTALLMENT_STATUS.PAGO) continue;
          const id = idForIndex(i);
          if (!id) continue;
          if (originalPaidIds.has(id)) continue; // já estava pago em DB
          const { error: payErr } = await markInstallmentAsPaid(id, {
            payment_date: rows[i].payment_date || todayISO(),
            payment_method: rows[i].payment_method,
            bank_id: rows[i].bank_id,
          });
          if (payErr) return payErr;
        }
        return null;
      };

      // Estratégia de parcelas:
      // - criação: insert em lote (status PENDENTE forçado) + markPaid pros escolhidos
      // - edição livre (sem parcela paga): apaga pendentes, reinsere e markPaid
      // - edição travada (com parcela paga): só atualiza linhas pendentes; pode
      //   promover pending → pago via markPaid (PAGO original é preservado)
      if (!isEdit) {
        const payloads = installments.map((inst) => ({
          financial_entry_id: savedEntryId,
          installment_number: inst.installment_number,
          due_date: inst.due_date,
          value: parseMoneyValue(inst.value),
          payment_method: inst.payment_method || null,
          bank_id: inst.bank_id || null,
          status: INSTALLMENT_STATUS.PENDENTE,
          created_by: user.id,
        }));
        const { data: insertedRows, error: instError } = await saveInstallments(payloads);
        if (instError) {
          alertError('Erro ao salvar parcelas.');
          return;
        }
        const payErr = await markPaidFor(installments, (i) => insertedRows?.[i]?.id);
        if (payErr) {
          alertError('Erro ao marcar parcelas como pagas.');
          return;
        }
      } else if (!lockSaldo) {
        const { error: delError } = await deletePendingInstallmentsByEntry(
          savedEntryId
        );
        if (delError) {
          alertError('Erro ao atualizar parcelas.');
          return;
        }
        const payloads = installments.map((inst) => ({
          financial_entry_id: savedEntryId,
          installment_number: inst.installment_number,
          due_date: inst.due_date,
          value: parseMoneyValue(inst.value),
          payment_method: inst.payment_method || null,
          bank_id: inst.bank_id || null,
          status: INSTALLMENT_STATUS.PENDENTE,
          created_by: user.id,
        }));
        const { data: insertedRows, error: instError } = await saveInstallments(payloads);
        if (instError) {
          alertError('Erro ao salvar parcelas.');
          return;
        }
        const payErr = await markPaidFor(installments, (i) => insertedRows?.[i]?.id);
        if (payErr) {
          alertError('Erro ao marcar parcelas como pagas.');
          return;
        }
      } else {
        // lockSaldo: parcelas PAGAS originais são editáveis e reconciliam o
        // Bank.balance via updatePaidInstallment (RN06). Pendentes recebem
        // update normal. Promoção pendente→PAGO via markInstallmentAsPaid.
        for (const inst of installments) {
          if (!inst.id) continue;
          if (originalPaidIds.has(inst.id)) {
            // Estorno (RN06/RN07): parcela era PAGA e o usuário voltou para
            // PENDENTE no dropdown. Reverte o efeito no saldo, limpa os dados
            // de pagamento e persiste eventuais correções de valor/vencimento.
            if (inst.status === INSTALLMENT_STATUS.PENDENTE) {
              const { error: revErr } = await revertInstallmentToPending(
                inst.id,
                user.id
              );
              if (revErr) {
                alertError('Erro ao estornar parcela.');
                return;
              }
              const { error: upErr } = await saveInstallment(
                inst.id,
                {
                  due_date: inst.due_date,
                  value: parseMoneyValue(inst.value),
                  payment_method: null,
                  bank_id: null,
                },
                user.id
              );
              if (upErr) {
                alertError('Erro ao atualizar parcela estornada.');
                return;
              }
              continue;
            }
            const { error: payUpErr } = await updatePaidInstallment(
              inst.id,
              {
                due_date: inst.due_date,
                value: parseMoneyValue(inst.value),
                payment_method: inst.payment_method || null,
                bank_id: inst.bank_id || null,
                payment_date: inst.payment_date || null,
              },
              user.id
            );
            if (payUpErr) {
              alertError('Erro ao atualizar parcela paga.');
              return;
            }
            continue;
          }
          const { error: upErr } = await saveInstallment(
            inst.id,
            {
              due_date: inst.due_date,
              value: parseMoneyValue(inst.value),
              payment_method: inst.payment_method || null,
              bank_id: inst.bank_id || null,
            },
            user.id
          );
          if (upErr) {
            alertError('Erro ao atualizar parcelas.');
            return;
          }
        }
        const payErr = await markPaidFor(installments, (i) => installments[i].id);
        if (payErr) {
          alertError('Erro ao marcar parcelas como pagas.');
          return;
        }
      }

      // Cobrança de OS: mantém a separação da DRE consistente com o novo total,
      // re-rateando o breakdown proporcionalmente (só quando o total mudou).
      if (isOSCharge) {
        const breakdownTotal = breakdown.reduce(
          (s, r) => s + (Number(r.value) || 0),
          0
        );
        if (Math.abs(entryTotalValue - breakdownTotal) > 0.005) {
          const rescaled = rescaleBreakdown(breakdown, entryTotalValue);
          const { error: bdErr } = await replaceBreakdownRows(
            savedEntryId,
            rescaled,
            user.id
          );
          if (bdErr) {
            alertError('Erro ao atualizar a separação da DRE.');
            return;
          }
        }
      }

      alertSuccess(
        isEdit ? 'Lançamento atualizado!' : 'Lançamento salvo com sucesso!'
      );
      if (onSaved) onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <div className="fd-modal-overlay" onClick={onClose}>
      <div
        className="fd-modal fd-modal--scroll"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="fd-modal-header">
          <h3 className="fd-modal-title">
            {isEdit ? 'Editar Lançamento' : 'Lançamento Financeiro'}
          </h3>
          <button
            className="fd-btn-close"
            type="button"
            onClick={onClose}
            style={{
              background: '#ED2D2D',
              color: '#fff',
              borderColor: '#ED2D2D',
            }}
          >
            X CANCELAR
          </button>
        </div>

        {/* BODY */}
        <div className="fd-form-body">
          {isEdit && osRateio && (
            <div
              style={{
                background: 'rgba(96,180,246,0.10)',
                border: '1px solid rgba(96,180,246,0.35)',
                color: '#60b4f6',
                padding: '8px 12px',
                borderRadius: 4,
                fontSize: 12,
                marginBottom: 12,
              }}
            >
              Este lançamento é 1 das {osRateio.count} contas do rateio da{' '}
              {form.document || 'OS'} — cobrança total de{' '}
              {formatCurrency(osRateio.total)}. Alterações feitas aqui afetam
              apenas esta conta.
            </div>
          )}

          {lockSaldo && (
            <div
              style={{
                background: 'rgba(232,140,42,0.10)',
                border: '1px solid rgba(232,140,42,0.35)',
                color: '#e88c2a',
                padding: '8px 12px',
                borderRadius: 4,
                fontSize: 12,
                marginBottom: 12,
              }}
            >
              Este lançamento já tem parcela paga. Você pode corrigir valor,
              vencimento, banco e dados — o saldo é recalculado automaticamente.
              Para estornar, mude a Situação da parcela para Pendente (aplicado ao
              salvar). Tipo e quantidade de parcelas permanecem bloqueados.
            </div>
          )}

          {/* TIPO TOGGLE */}
          <div
            className="fd-entry-type-toggle"
            title={classificationTooltip}
            style={lockClassification ? { opacity: 0.55, pointerEvents: 'none' } : undefined}
          >
            <button
              type="button"
              disabled={lockClassification}
              className={`fd-entry-type-btn ${type === ENTRY_TYPE.RECEITA ? 'fd-entry-type-btn--active-green' : ''}`}
              onClick={() => handleChangeType(ENTRY_TYPE.RECEITA)}
            >
              RECEITA
            </button>
            <button
              type="button"
              disabled={lockClassification}
              className={`fd-entry-type-btn ${type === ENTRY_TYPE.DESPESA ? 'fd-entry-type-btn--active-green' : ''}`}
              onClick={() => handleChangeType(ENTRY_TYPE.DESPESA)}
            >
              DESPESA
            </button>
          </div>

          {/* CAMPOS */}
          <div className="fd-form-row fd-form-row--with-attach">
            <LabelInput
              label="Documento"
              value={form.document}
              placeholder="Nº do documento"
              max={50}
              onChange={(value) => onChange('document', value)}
            />

            <DocumentAttach
              value={form.document_url}
              onChange={(v) => onChange('document_url', v)}
            />

            {/* RN-002: Data de Emissão editável, inclusive em modo edição. */}
            <LabelInput
              label="Data Emissão"
              type="date"
              value={form.issue_date}
              onChange={(value) => onChange('issue_date', value)}
            />
          </div>

          <div className="fd-form-row">
            {renderSupplierField()}
            {!isOSCharge && (
              <LabelSelect
                menuPortalTarget={document.body}
                label="Plano de Contas *"
                options={accountPlanOptions}
                value={findSelectedAccountOption(
                  accountPlanOptions,
                  form.account_plan_id
                )}
                placeholder="Selecione uma conta"
                onChange={(opt) => handleSelectAccountPlan(opt?.value || null)}
              />
            )}
          </div>

          {isOSCharge && renderBreakdownPanel()}

          <div className="fd-form-row">
            {/* RN-006: Centro de Custo oculto até futura implementação. */}
            <MoneyInput
              label="Valor Total"
              value={form.total_value}
              placeholder="R$ 0,00"
              onChange={(value) => onChange('total_value', value)}
            />
          </div>

          <LabelInput
            label="Descrição"
            value={form.description}
            placeholder="Descrição do lançamento..."
            max={500}
            onChange={(value) => onChange('description', value)}
          />

          {/* PARCELAS */}
          <div className="fd-entry-installments-header">
            <span className="fd-entry-installments-title">Parcelas</span>
            <div className="fd-entry-installments-controls">
              <span style={{ fontSize: 12, color: '#ccc' }}>Qtd:</span>
              <input
                type="number"
                className="fd-installment-input"
                style={{
                  width: 55,
                  textAlign: 'center',
                  ...(lockSaldo
                    ? { opacity: 0.55, cursor: 'not-allowed' }
                    : {}),
                }}
                value={installmentCount}
                min={1}
                max={48}
                disabled={lockSaldo}
                title={lockTooltip}
                onChange={(e) => setInstallmentCount(e.target.value)}
                onBlur={(e) => {
                  const num = Number(e.target.value) || 1;
                  setInstallmentCount(Math.max(1, Math.min(48, num)));
                }}
              />
              <button
                className="fd-btn-green"
                type="button"
                disabled={lockSaldo}
                title={lockTooltip}
                onClick={generateInstallments}
                style={{
                  padding: '5px 12px',
                  fontSize: 11,
                  ...(lockSaldo
                    ? { opacity: 0.55, cursor: 'not-allowed' }
                    : {}),
                }}
              >
                GERAR
              </button>
            </div>
          </div>

          <InstallmentTable
            installments={installments}
            bankOptions={bankOptions}
            onUpdate={setInstallments}
            lockedIds={originalPaidIds}
          />
        </div>

        {/* FOOTER */}
        <div className="fd-modal-footer">
          {isEdit && (
            <button
              className="fd-btn-danger"
              type="button"
              onClick={onDelete || handleDeleteEntry}
              disabled={saving || loading || lockSaldo}
              title={
                lockSaldo
                  ? 'Não pode excluir: este lançamento tem parcela paga.'
                  : undefined
              }
              style={{ marginRight: 'auto' }}
            >
              Excluir
            </button>
          )}
          <button
            className="fd-btn-close"
            type="button"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            className="fd-btn-green"
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving
              ? 'SALVANDO...'
              : loading
                ? 'CARREGANDO...'
                : isEdit
                  ? 'SALVAR ALTERAÇÕES'
                  : 'SALVAR LANÇAMENTO'}
          </button>
        </div>
      </div>
    </div>

    <SupplierFormModal
      isOpen={supplierModal.open}
      supplierId={supplierModal.id}
      companyId={company.id}
      userId={user.id}
      onClose={() => setSupplierModal({ open: false, id: null })}
      onSaved={onSupplierSaved}
    />

    <BudgetAddClient
      isOpen={clientModal.open}
      currentClientId={clientModal.id}
      origins={origins}
      createOrigin={handleCreateOrigin}
      setOpen={() => setClientModal((m) => ({ ...m, open: false }))}
      setReload={() => setRefModalKey((k) => k + 1)}
      onSaved={onClientSaved}
    />
    </>
  );
};

export default FinancialEntryModal;
